export const MAX_SCALE_FACTORS = 120;
export const TNS_MAX_ORDER = 20;


interface Element {
  element_instance_tag: number;
}


export interface TemporalNoiseShaping {
  n_filt: number[];       // int[8]
  length: number[][];     // int[8][4]
  direction: number[][];  // int[8][4]
  order: number[][];      // int[8][4]
  coef: number[][][];     // float[8][4][TNS_MAX_ORDER]
}


export interface CommonChannelElement extends Element {
  type: string;
}

export interface SingleChannelElement extends CommonChannelElement {
  type: 'SCE';
  channel0: ChannelElement;
}


export interface ChannelPairElement extends CommonChannelElement {
  type: 'CPE';
  channel0: ChannelElement;
  channel1: ChannelElement;
}


export enum CouplingPoint {
  BEFORE_TNS,
  BETWEEN_TNS_AND_IMDCT,
  AFTER_IMDCT = 3,
}


export interface CouplingChannelElement extends CommonChannelElement {
  type: 'CCE';
  couplingPoint: CouplingPoint;
  channel0: ChannelElement;
}


export interface LfeChannelElement extends CommonChannelElement {
  type: 'LFE';
  channel0: ChannelElement;
}


export interface DataStreamElement extends Element {
  type: 'DSE';
}


export interface ProgramConfigElement extends Element {
  type: 'PCE';
  profile: number;
  sampling_frequency_index: number;

  mono_mixdown_element_number: number | undefined;
  stereo_mixdown_element_number: number | undefined;
  matrix_mixdown_idx: number | undefined;
  pseudo_surround_enable: boolean | undefined;

  front_element_is_cpe: boolean[];
  front_element_tag_select: number[];
  side_element_is_cpe: boolean[];
  side_element_tag_select: number[];
  back_element_is_cpe: boolean[];
  back_element_tag_select: number[];
  lfe_element_tag_select: number[];
  assoc_data_element_tag_select: number[];
  cc_element_is_ind_sw: boolean[];
  valid_cc_element_tag_select: number[];

  comment_field_data: number[];
}


export type AacElement =
  SingleChannelElement
| ChannelPairElement
| CouplingChannelElement
| LfeChannelElement
| DataStreamElement
| ProgramConfigElement;


export interface ChannelElement {
  coeffs: Float32Array;
  ics_info: IcsInfo,
  scalefactorBandsAndGroupingInfo: ScalefactorBandsAndGroupingInfo,
  section_data_info: SectionDataInfo;
  scaleFactors: Float32Array; // MAX_SCALE_FACTORS
  tns: TemporalNoiseShaping | undefined;
  channelIndex: number;
  samples: Float32Array; // 1024 samples
  overlaps: Float32Array;
}


export interface IcsInfo {
  previous_window_sequence: number;
  current_window_sequence: number;
  previous_window_shape: number;
  current_window_shape: number;
  max_sfb: number;
  scale_factor_grouping: number;
  predictor_data_present: number;
  predictor_reset: number;
  predictor_reset_group_number: number;
  prediction_used: number[];
}


export interface ScalefactorBandsAndGroupingInfo {
  num_windows: number;
  num_window_groups: number;
  window_group_length: number[];
  num_swb: number;
  sect_sfb_offset: number[][];
  swb_offset: number[];
  tns_max_bands: number;
}


export interface SectionDataInfo {
  band_type: number[];
  band_type_run_end: number[];
}


export function isSCE(e: CommonChannelElement): e is SingleChannelElement {
  return e.type === 'SCE';
}


export function isCPE(e: CommonChannelElement): e is ChannelPairElement {
  return e.type === 'CPE';
}


export function isLFE(e: CommonChannelElement): e is LfeChannelElement {
  return e.type === 'LFE';
}


export function isCCE(e: CommonChannelElement): e is CouplingChannelElement {
  return e.type === 'CCE';
}


export interface DynamicRangeInfo {
  n: number;

  pce_tag_present: number;
  pce_instance_tag: number;
  drc_tag_reserved_bits: number;
  excluded_chns_present: number;
  drc_bands_present: number;
  drc_band_incr: number;
  drc_bands_reserved_bits: number;
  drc_band_top: number[];
  prog_ref_level_present: number;
  prog_ref_level: number;
  prog_ref_level_reserved_bits: number;
  dyn_rng_sgn: number[];
  dyn_rng_ctl: number[];
}


export interface ExcludeChannelsInfo {
  n: number;

  exclude_mask: number[];
  additional_excluded_chns: number[];
}


export interface SbrExtensionData {
  n: number;
  bs_sbr_crc_bits: number;
  num_sbr_bits: number;
  num_align_bits: number;
}


export interface SbrHeader {
  nBits: number;
  bs_amp_res: number;
  bs_start_freq: number;
  bs_stop_freq: number;
  bs_xover_band: number;
  bs_header_extra_1: number;
  bs_header_extra_2: number;
  bs_freq_scale: number;
  bs_alter_scale: number;
  bs_noise_bands: number;
  bs_limiter_bands: number;
  bs_limiter_gains: number;
  bs_interpol_freq: number;
  bs_smoothing_mode: number;
}


export interface SbrData {
  nBits: number;
}


export interface Pulse {
  num_pulse: number;
  start: number;
  pos: number[];
  amp: number[];
}


/**
 * 32-bits float samples between -1.0 and 1.0,
 * one sample array per channel.
 */
export interface DecodedPcmFloat32Channels {
  sampleRateInHz: number;
  channels: Float32Array[];
}
